import BrandLogo from '@/components/common/BrandLogo';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, CreditCard, Settings } from 'lucide-react';
import { Select, SelectContent, SelectItem } from '@/components/ui/select';
import * as SelectPrimitive from '@radix-ui/react-select';
import DashboardIcon from '../icons/DashboardIcon';
import TutorialsIcon from '../icons/TutorialsIcon';
import CurvesIcon from '../icons/CurvesIcon';
import UsersIcon from '../icons/UsersIcon';
import LocationIcon from '../icons/LocationIcon';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/app/store';
import { switchWorkspace } from '@/features/workspace/workspaceSlice';
import { fetchUserWorkspaces } from '@/features/workspace/workspaceThunks';
import type { Workspace } from '@/features/workspace/types';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useCallback, useState } from 'react';
import { Pagination } from '../ui/pagination';
import { LanguageFlag } from '@/components/ui/FlagIcon';
import { APP_LANGUAGES, getAppLanguage } from '@/i18n/languages';

const items = [
  { to: '/', icon: DashboardIcon, key: 'dashboard' },
  { to: '/webinars', icon: TutorialsIcon, key: 'webinars' },
  { to: '/analytics', icon: CurvesIcon, key: 'analytics' },
  { to: '/team', icon: UsersIcon, key: 'team' },
  { to: '/integrations', icon: LocationIcon, key: 'integrations' },
  { to: '/settings', icon: Settings, key: 'settings' },
  { to: '/billing', icon: CreditCard, key: 'billing' },
] as const;

export default function SidebarPanel({}: Readonly<{
  onNavigate?: () => void;
}>) {
  const { t, i18n } = useTranslation('common');
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);
  const workspacesList = useSelector((state: RootState) => state.workspace.workspacesList);
  const workspacesTotalPages = useSelector(
    (state: RootState) => state.workspace.workspacesTotalPages,
  );
  const workspaceLoading = useSelector((state: RootState) => state.workspace.loading);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  const totalPages = workspacesTotalPages ?? 1;
  const showWorkspaceSwitcher =
    workspaceLoading || (workspacesList != null && (totalPages > 1 || workspacesList.length > 1));

  const onOpenChange = useCallback(
    (open: boolean) => {
      setModalOpen(open);
      if (open && workspacesList == null) {
        void dispatch(fetchUserWorkspaces({ page: currentPage }));
      }
    },
    [dispatch, workspacesList, currentPage],
  );

  const handleWorkspaceListPageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      void dispatch(fetchUserWorkspaces({ page }));
    },
    [dispatch],
  );

  const workspaces = workspacesList ?? [];
  const listLoading = workspaceLoading;

  const handleSwitchWorkspace = useCallback(
    (workspace: Workspace) => {
      dispatch(
        switchWorkspace({
          ...workspace,
          socials: workspace.socials ?? [],
          visualIdentity: workspace.visualIdentity ?? null,
        }),
      );
      navigate('/');
      setModalOpen(false);
    },
    [dispatch, navigate],
  );

  const currentLanguage = getAppLanguage(i18n.resolvedLanguage ?? i18n.language);
  const currentLang = currentLanguage.code;

  return (
    <div
      className="
        relative h-full w-full sticky top-0 overflow-hidden
        rounded-[20px]
        text-white
        flex flex-col
        px-4 py-6
      "
    >
      <div className="pointer-events-none absolute inset-0 rounded-[20px] bg-dark " />
      <div className="pointer-events-none absolute -left-10 -top-10 h-[240px] w-[260px] rounded-full " />
      <div className="relative z-10 flex min-h-0 flex-1 flex-col">
        <div className="px-2">
          <BrandLogo />
        </div>

        <nav className="mt-12 flex-1 overflow-y-auto pr-1 space-y-6">
          {items.map(({ to, icon: Icon, key }) => (
            <NavLink
              key={key}
              to={to}
              className={({ isActive }) =>
                [
                  'group relative z-20 flex w-full items-center gap-3 rounded-full px-4 py-2',
                  'text-white',
                  !isActive &&
                    'hover:text-white hover:bg-white/10 hover:outline hover:outline-white/12',
                  isActive && 'bg-decorative-highlighted',
                ]
                  .filter(Boolean)
                  .join(' ')
              }
              style={({ isActive }) =>
                isActive ? { backgroundColor: 'var(--color-primary-500)' } : undefined
              }
              end={to === '/'}
            >
              <Icon className="size-6" />
              <span className="text-sm font-medium">{t(`sidebar.${key}`)}</span>
            </NavLink>
          ))}
        </nav>

        <Select value={currentLang} onValueChange={(val) => void i18n.changeLanguage(val)}>
          <SelectPrimitive.Trigger
            aria-label={t('changeLanguage')}
            className="mt-4 mb-3 w-full flex items-center bg-white/10 rounded-full p-2.5 gap-3 text-white text-sm font-semibold outline-none hover:bg-white/15 cursor-pointer [&[data-state=open]>svg:last-child]:rotate-180"
          >
            <LanguageFlag
              code={currentLang}
              className="w-6 h-6 rounded-xs overflow-hidden shrink-0"
            />
            <span className="flex-1 text-left">{currentLanguage.nativeLabel}</span>
            <ChevronDown className="size-4 text-white/50 shrink-0 transition-transform duration-200" />
          </SelectPrimitive.Trigger>
          <SelectContent side="top">
            {APP_LANGUAGES.map(({ code, nativeLabel }) => (
              <SelectItem key={code} value={code}>
                <div className="flex items-center gap-2">
                  <LanguageFlag
                    code={code}
                    className="w-5 h-3.5 rounded-[1px] overflow-hidden shrink-0"
                  />
                  <span>{nativeLabel}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="mt-0">
          <button
            type="button"
            onClick={() => onOpenChange(true)}
            className="flex w-full cursor-pointer items-center gap-2 rounded-full bg-white/10 p-2.5 text-left hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/20"
          >
            <div className="h-6 w-6 shrink-0 rounded-full bg-gradient-brand" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{currentWorkspace?.name ?? '—'}</p>
            </div>
          </button>

          <Dialog open={modalOpen} onOpenChange={onOpenChange}>
            <DialogContent className="">
              <DialogHeader>
                <DialogTitle className="pb-2 text-xl">
                  {t('sidebar.workspaceModal.title', 'Workspace')}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* Workspace actuel en haut */}
                {currentWorkspace && (
                  <div
                    className="rounded-xl border-2 border-highlighted bg-decorative-2 p-4 flex items-center gap-3"
                    aria-label={currentWorkspace.name}
                  >
                    <div className="size-10 shrink-0 rounded-full bg-gradient-brand" aria-hidden />
                    <p className="font-medium text-xl truncate">
                      {currentWorkspace.name} <span className="text-sm text-tertiary">(Admin)</span>
                    </p>
                  </div>
                )}

                {/* Changer de Workspace + liste */}
                {showWorkspaceSwitcher && (
                  <>
                    <div className="space-y-4">
                      <p className="text-sm font-medium text-secondary">
                        {t('sidebar.workspaceModal.changeWorkspace', 'Changer de Workspace')}
                      </p>
                      {listLoading ? (
                        <p className="py-4 text-center text-sm text-muted-foreground">
                          {t('loading')}
                        </p>
                      ) : (
                        <div className="space-y-4 w-full ">
                          {workspaces
                            .filter((workspace) => workspace.id !== currentWorkspace?.id)
                            .map((workspace) => (
                              <div
                                key={workspace.id}
                                className="bg-[#1F1F1F]/3 w-full flex items-center justify-between gap-2 p-4 rounded-[20px]"
                              >
                                <div className="flex items-center gap-2">
                                  <div
                                    className="size-10 shrink-0 rounded-full bg-gradient-brand"
                                    aria-hidden
                                  />
                                  <p className="font-medium text-xl truncate">
                                    {workspace.name}{' '}
                                    <span className="text-sm text-tertiary">(Admin)</span>
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    onClick={() => handleSwitchWorkspace(workspace)}
                                  >
                                    {t('sidebar.workspaceModal.switchButton', 'Se connecter')}
                                  </Button>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handleWorkspaceListPageChange}
                    />
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
